import { ApiButton, Box, Grid, Icon, Typography } from 'web-library';
import { IconName } from 'web-library/stories/atom/Icon/Icon';
import IconWithLabel from 'web-library/stories/atom/IconWithLabel/IconWithLabel';
import React from 'react';

import { makeStyles } from 'tss-react/mui';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@mui/material';
import {
  DownloadEntryProofResponse,
  RegistrationType,
  useGetDownloadEntryProof,
} from '../../../api-refactored/entry-proof/useGetDownloadEntryProof';

const useStyles = makeStyles()((theme) =>
({
  actionButton: {
    padding: 0,
    margin: 0,
    alignItems: 'center',
    marginBottom: theme.spacing(4),
    [theme.breakpoints.down('md')]: {
      paddingBottom: theme.spacing(5),
    },
    '& .MuiButton-label': {
      justifyContent: 'flex-start',
    },
    '& .MuiButton-label div': {
      maxWidth: 400,
    },
  },
  entryProofContainer: {
    height: theme.spacing(4),
    marginBottom: theme.spacing(4),
    [theme.breakpoints.down('md')]: {
      marginBottom: theme.spacing(7),
    },
  },
  fileContainer: {
    display: 'inline-flex',
    marginBottom: theme.spacing(4),
  },
  iconContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'start',
    justifySelf: 'start',
    marginRight: theme.spacing(2),
  },
  documentIcon: {
    color: theme.color.text.textTertiary,
  },
  filename: {
    color: theme.color.text.textPrimary,
  },
}));

interface EntryProofIconProps {
  fileName: string;
  uuid: string;
  registrationType: RegistrationType;
}

const EntryProofIcon = ({ fileName, uuid, registrationType }: EntryProofIconProps) => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const theme = useTheme();

  const {
    refetch: downloadEntryProof,
    isFetching: isFetchingEntryProof,
    status: GetDownloadEntryProofStatus,
  } = useGetDownloadEntryProof({
    downloadEntryProofRequest: { uuid, registrationType },
    onSuccess: (download: DownloadEntryProofResponse) => {
      const newWindow = window.open(download?.url, '_blank', 'noopener,noreferrer');
      if (newWindow) {
        newWindow.opener = null;
      }
    },
  });

  return (
    (<Grid
      container
      direction="row"
      alignItems="center"
      justify="flex-start"
      className={classes.entryProofContainer}
      key={uuid}
    >
      <Grid md={6} sm={12} xs={12} className={classes.fileContainer}>
        <Box className={classes.iconContainer}>
          <Icon size="L" name={IconName.document} className={classes.documentIcon} />
        </Box>
        <Typography noWrap className={classes.filename}>
          {fileName}
        </Typography>
      </Grid>
      <Grid md={4} sm={12} xs={12}>
        {isFetchingEntryProof ? (
          <Icon
            className={classes.actionButton}
            name={IconName.loadingIndicator}
            size="L"
            color={theme.color.link.linkActive02}
          />
        ) : (
          <ApiButton
            className={classes.actionButton}
            variant="text"
            color="primary"
            status={GetDownloadEntryProofStatus}
            size="small"
            onClick={() => downloadEntryProof()}
          >
            <IconWithLabel
              iconName={IconName.download}
              iconSize="M"
              label={t('exam->>RegistrationSummary.Download')}
              typographyProps={{
                variant: 'h5',
              }}
            />
          </ApiButton>
        )}
      </Grid>
    </Grid>)
  );
};

export default EntryProofIcon;
